import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell
} from '@mui/material';
import { Badge } from '@mui/material'; // Assuming Badge is part of @mui/material
import { DollarSign, Package, TrendingUp, Plus } from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import { BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip } from 'recharts';
import { SalesByItemReportResponse } from 'src/services/apiService/response-models/report';


interface ItemSalesSummaryProps {
    itemSalesSummary: SalesByItemReportResponse[] | null | undefined;
}

const SalesSummary: React.FC<ItemSalesSummaryProps> = ({ itemSalesSummary }: ItemSalesSummaryProps) => {
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'itemName', direction: 'asc' });

  // Calculate summary values
  const totalRevenue = itemSalesSummary.reduce(
    (sum, item) => sum + item.totalSales,
    0
  );
  const totalQuantity = itemSalesSummary.reduce(
    (sum, item) => sum + item.quantitySold,
    0
  );
  const totalModifierRevenue = itemSalesSummary.reduce(
    (sum, item) =>
      sum +
      (item.addOnIds?.reduce(
        (a, addOn) => a + (addOn.addOnSalesTotal || 0),
        0
      ) || 0),
    0
  );
  const avgOrderValue = totalQuantity ? totalRevenue / totalQuantity : 0;

  // Prepare data for bar chart
  // const itemSalesData = sampleData.map((item) => ({
  // 	name: item.itemName,
  // 	sales: item.totalSales,
  // }));

  // Prepare chart data
  const itemSalesData = itemSalesSummary.map((item) => ({
    name:
      item.itemName.length > 20
        ? item.itemName.substring(0, 20) + '...'
        : item.itemName,
    sales: item.totalSales,
    quantity: item.quantitySold
  }));

  // Sum addOnSalesTotal for similar addOns by name
  const addOnDataMap = new Map<string, { name: string; value: number }>();
  itemSalesSummary.forEach((item) => {
    item.addOnIds.forEach((addOn) => {
      if (addOnDataMap.has(addOn.addOnName)) {
        addOnDataMap.get(addOn.addOnName)!.value += addOn.addOnSalesTotal;
      } else {
        addOnDataMap.set(addOn.addOnName, {
          name: addOn.addOnName,
          value: addOn.addOnSalesTotal
        });
      }
    });
  });
  const addOnData = Array.from(addOnDataMap.values());

  const COLORS = [
    '#2A9D90', // Equivalent to hsl(173 58% 39%)
    '#E76E50', // Equivalent to hsl(12 76% 61%)
    '#274754', // Equivalent to hsl(197 37% 24%)
    '#E8C468', // Equivalent to hsl(43 74% 66%)
    '#F4A462' // Equivalent to hsl(27 87% 67%)
  ];

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const sortedItems = [...itemSalesSummary].sort((a, b) => {
    const { key, direction } = sortConfig;
    let aValue: any = a[key];
    let bValue: any = b[key];
    if (key === 'itemName') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    if (aValue < bValue) return direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div style={{ padding: 24 }}>
      <h2>Sales Summary Report</h2>
      {/* Summary Cards */}
      <div
        className="grid gap-4 md:grid-cols-2 lg:grid-cols-4"
        style={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          marginBottom: 32
        }}
      >
        <Card>
          <CardHeader
            title={<span className="text-sm font-medium">Total Revenue</span>}
            action={<DollarSign size={20} style={{ color: '#888' }} />}
            style={{ paddingBottom: 0 }}
          />
          <CardContent>
            <div
              className="text-2xl font-bold"
              style={{ fontSize: 28, fontWeight: 700 }}
            >
              ${totalRevenue.toFixed(2)}
            </div>
            <p
              className="text-xs text-muted-foreground"
              style={{ fontSize: 12, color: '#888' }}
            >
              Base: ${(totalRevenue - totalModifierRevenue).toFixed(2)} +
              Add-ons: ${totalModifierRevenue.toFixed(2)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader
            title={<span className="text-sm font-medium">Items Sold</span>}
            action={<Package size={20} style={{ color: '#888' }} />}
            style={{ paddingBottom: 0 }}
          />
          <CardContent>
            <div
              className="text-2xl font-bold"
              style={{ fontSize: 28, fontWeight: 700 }}
            >
              {totalQuantity}
            </div>
            <p
              className="text-xs text-muted-foreground"
              style={{ fontSize: 12, color: '#888' }}
            >
              Across {itemSalesSummary.length} different items
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader
            title={<span className="text-sm font-medium">Avg Order Value</span>}
            action={<TrendingUp size={20} style={{ color: '#888' }} />}
            style={{ paddingBottom: 0 }}
          />
          <CardContent>
            <div
              className="text-2xl font-bold"
              style={{ fontSize: 28, fontWeight: 700 }}
            >
              ${avgOrderValue.toFixed(2)}
            </div>
            <p
              className="text-xs text-muted-foreground"
              style={{ fontSize: 12, color: '#888' }}
            >
              Per item sold
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader
            title={<span className="text-sm font-medium">Add-on Revenue</span>}
            action={<Plus size={20} style={{ color: '#888' }} />}
            style={{ paddingBottom: 0 }}
          />
          <CardContent>
            <div
              className="text-2xl font-bold"
              style={{ fontSize: 28, fontWeight: 700 }}
            >
              ${totalModifierRevenue.toFixed(2)}
            </div>
            <p
              className="text-xs text-muted-foreground"
              style={{ fontSize: 12, color: '#888' }}
            >
              {totalRevenue
                ? ((totalModifierRevenue / totalRevenue) * 100).toFixed(1)
                : 0}
              % of total revenue
            </p>
          </CardContent>
        </Card>
      </div>
      {/* Charts Section */}
      <div
        className="grid gap-6 md:grid-cols-2"
        style={{
          display: 'grid',
          gap: 24,
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          marginBottom: 32
        }}
      >
        <Card>
          <CardHeader title={<span>Sales by Item</span>} />
          <CardContent>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={itemSalesData}
                  margin={{ top: 16, right: 16, left: 0, bottom: 40 }}
                >
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={80}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis />
                  <ChartTooltip
                    formatter={(value: number) => `$${value.toFixed(2)}`}
                  />
                  <Bar dataKey="sales" fill="#2A9D90" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader title={<span>Add-on Revenue Distribution</span>} />
          <CardContent>
            <div style={{ width: '100%', height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                {addOnData.length > 0 ? (
                  <PieChart>
                    <Pie
                      data={addOnData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {addOnData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number) => `$${value.toFixed(2)}`}
                    />
                    <Legend />
                  </PieChart>
                ) : (
                  <div style={{ textAlign: 'center', color: '#888', marginTop: 100 }}>
                    No add-on revenue data available.
                  </div>
                )}
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Table */}
      <div
        className="grid gap-6 md:grid-cols-1"
        style={{
          display: 'grid',
          gap: 24,
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          marginBottom: 32
        }}
      >
        <Card>
          <CardHeader title={<span>Detailed Item Performance</span>} />
          <CardContent>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell onClick={() => handleSort('itemName')} style={{ cursor: 'pointer' }}>
                    Item Name {sortConfig.key === 'itemName' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>
                  <TableCell align="right" onClick={() => handleSort('quantitySold')} style={{ cursor: 'pointer' }}>
                    Quantity Sold {sortConfig.key === 'quantitySold' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>
                  <TableCell align="right" onClick={() => handleSort('avgPrice')} style={{ cursor: 'pointer' }}>
                    Avg Price {sortConfig.key === 'avgPrice' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>
                  <TableCell align="right" onClick={() => handleSort('totalSales')} style={{ cursor: 'pointer' }}>
                    Total Sales {sortConfig.key === 'totalSales' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>
                  <TableCell align="right" onClick={() => handleSort('modifierUsage')} style={{ cursor: 'pointer' }}>
                    Add-on Revenue {sortConfig.key === 'modifierUsage' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                  </TableCell>
                  <TableCell>Top Add-on</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedItems.map((item) => (
                  <TableRow key={item.itemId}>
                    <TableCell style={{ fontWeight: 500 }}>
                      {item.itemName}
                    </TableCell>
                    <TableCell align="right">{item.quantitySold}</TableCell>
                    <TableCell align="right">
                      ${item.avgPrice.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      ${item.totalSales.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      ${item.modifierUsage.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {item.addOnIds.length > 0 &&
                        item.addOnIds.map((addOn) => (
                          <span
                            key={addOn.addOnId}
                            style={{
                              display: 'inline-block',
                              background: '#e0e7ff',
                              color: '#3730a3',
                              borderRadius: 8,
                              padding: '2px 10px',
                              fontSize: 12,
                              fontWeight: 500
                            }}
                          >
                            {addOn.addOnName}
                          </span>
                        ))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
      {/* Add-on Analysis */}
      <div
        className="grid gap-6 md:grid-cols-1"
        style={{
          display: 'grid',
          gap: 24,
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          marginBottom: 32
        }}
      >
        <Card>
          <CardHeader title={<span>Add-on Performance Analysis</span>} />
          <CardContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {itemSalesSummary
                .filter((item) => item.addOnIds.length > 0)
                .map((item) => (
                  <div
                    key={item.itemId}
                    style={{
                      border: '1px solid #e5e7eb',
                      borderRadius: 12,
                      padding: 16
                    }}
                  >
                    <h4 style={{ fontWeight: 600, marginBottom: 8 }}>
                      {item.itemName}
                    </h4>
                    <div
                      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                    >
                      {item.addOnIds.map((addOn) => (
                        <div
                          key={addOn.addOnId}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <span style={{ fontSize: 14 }}>{addOn.addOnName}</span>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8
                            }}
                          >
                            <span
                              style={{
                                display: 'inline-block',
                                border: '1px solid #a5b4fc',
                                color: '#3730a3',
                                borderRadius: 8,
                                padding: '2px 10px',
                                fontSize: 12,
                                fontWeight: 500
                              }}
                            >
                              ${addOn.addOnSalesTotal.toFixed(2)}
                            </span>
                            <span style={{ fontSize: 12, color: '#6b7280' }}>
                              {(
                                (addOn.addOnSalesTotal / item.totalSales) *
                                100
                              ).toFixed(1)}
                              % of item sales
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SalesSummary;
